import React from "react";
import styled from "styled-components";

interface ValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  validationIssues: string[];
  hasErrors: boolean;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2<{ $hasErrors: boolean }>`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.$hasErrors ? "#dc2626" : "#059669")};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }
`;

const ValidationList = styled.div`
  margin-bottom: 24px;
`;

const ValidationItem = styled.div<{ $isError: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const ValidationIcon = styled.div<{ $isError: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  background: ${(props) => (props.$isError ? "#fef2f2" : "#f0fdf4")};
  color: ${(props) => (props.$isError ? "#dc2626" : "#059669")};
  border: 2px solid ${(props) => (props.$isError ? "#fecaca" : "#bbf7d0")};
`;

const ValidationText = styled.span<{ $isError: boolean }>`
  color: ${(props) => (props.$isError ? "#374151" : "#374151")};
  font-size: 14px;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ $variant: "primary" | "secondary" | "danger" }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) => {
    switch (props.$variant) {
      case "primary":
        return `
          background: #3b82f6;
          color: white;
          &:hover {
            background: #2563eb;
          }
        `;
      case "secondary":
        return `
          background: #f3f4f6;
          color: #374151;
          &:hover {
            background: #e5e7eb;
          }
        `;
      case "danger":
        return `
          background: #dc2626;
          color: white;
          &:hover {
            background: #b91c1c;
          }
        `;
    }
  }}
`;

const SummaryText = styled.p`
  margin: 0 0 16px 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
`;

const ValidationModal: React.FC<ValidationModalProps> = ({
  isOpen,
  onClose,
  onProceed,
  validationIssues,
  hasErrors,
}) => {
  const errorCount = validationIssues.filter((issue) =>
    issue.startsWith("❌")
  ).length;
  const successCount = validationIssues.filter((issue) =>
    issue.startsWith("✅")
  ).length;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle $hasErrors={hasErrors}>
            {hasErrors ? (
              <>❌ Approval Requirements Not Met</>
            ) : (
              <>✅ All Requirements Met</>
            )}
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <SummaryText>
          {hasErrors
            ? `Your map needs ${errorCount} more requirement${
                errorCount !== 1 ? "s" : ""
              } before it can be approved.`
            : `Great! Your map meets all ${successCount} approval requirements.`}
        </SummaryText>

        <ValidationList>
          {validationIssues.map((issue, index) => {
            const isError = issue.startsWith("❌");
            const cleanText = issue.replace(/^[❌✅]\s*/, "");

            return (
              <ValidationItem key={index} $isError={isError}>
                <ValidationIcon $isError={isError}>
                  {isError ? "✕" : "✓"}
                </ValidationIcon>
                <ValidationText $isError={isError}>{cleanText}</ValidationText>
              </ValidationItem>
            );
          })}
        </ValidationList>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            {hasErrors ? "Close" : "Cancel"}
          </Button>
          {!hasErrors && (
            <Button $variant="primary" onClick={onProceed}>
              Submit for Approval
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ValidationModal;
